/**
 * Fullscreen API guard.
 *
 * iPhone Safari has no `Element.requestFullscreen` (only <video> can go
 * fullscreen there). Calling it threw synchronously — "requestFullscreen is
 * not a function" — BEFORE the promise `.catch()` the viewers relied on, so
 * every tap on the Full Screen button was a reported crash. The viewers now
 * hide the button when the API is missing and never call what isn't there.
 */

import { reportCapabilityGap } from './errorReporting';

/** True when this browser can put an arbitrary element into fullscreen. */
export function fullscreenSupported(): boolean {
  if (typeof document === 'undefined') return false;
  const proto = typeof Element !== 'undefined' ? Element.prototype : null;
  if (!proto || typeof (proto as Element).requestFullscreen !== 'function') return false;
  // `fullscreenEnabled` is false when a permissions policy or an iframe
  // without allowfullscreen blocks it; undefined in very old browsers (allow).
  return document.fullscreenEnabled !== false;
}

let gapReported = false;

/**
 * Enter fullscreen on `el` if the API exists; resolves false when it doesn't
 * or the request is denied. Never throws.
 */
export async function requestFullscreenSafe(el: Element | null): Promise<boolean> {
  if (!el) return false;
  if (!fullscreenSupported() || typeof el.requestFullscreen !== 'function') {
    if (!gapReported) {
      gapReported = true;
      reportCapabilityGap('fullscreen');
    }
    return false;
  }
  try {
    await el.requestFullscreen();
    return true;
  } catch {
    return false;
  }
}

/** Leave fullscreen if we're in it. Never throws. */
export async function exitFullscreenSafe(): Promise<boolean> {
  if (typeof document === 'undefined' || !document.fullscreenElement) return false;
  if (typeof document.exitFullscreen !== 'function') return false;
  try {
    await document.exitFullscreen();
    return true;
  } catch {
    return false;
  }
}
